import React, { useMemo, useState } from "react";
import { Activity, Gauge } from "lucide-react";
import { updateCoachingAthlete } from "../../lib/api";
import {
  computeVdot,
  predictRaceSeconds,
  trainingPaces,
  secToHMS,
  paceRangeLabel,
  REFERENCE_RACES,
} from "../../lib/vdot";
import { toast } from "sonner";

const RACE_PRESETS = [
  { label: "5K", meters: 5000 },
  { label: "10K", meters: 10000 },
  { label: "Half", meters: 21097.5 },
  { label: "Marathon", meters: 42195 },
  { label: "Custom", meters: null },
];

function parseMMSS(text) {
  const t = (text || "").trim();
  if (!t) return null;
  const parts = t.split(":").map((p) => parseInt(p, 10));
  if (parts.some((n) => isNaN(n))) return null;
  if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  if (parts.length === 2) return parts[0] * 60 + parts[1];
  if (parts.length === 1) return parts[0] * 60;
  return null;
}

function formatDistanceLabel(m) {
  if (!m) return "—";
  if (m >= 42195) return "Marathon";
  if (m >= 21097) return "Half Marathon";
  if (m % 1000 === 0) return `${m / 1000}K`;
  return `${(m / 1000).toFixed(1)}K`;
}

export default function PaceProfile({ athlete, onChange }) {
  const existing = athlete.pace_profile;
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [preset, setPreset] = useState(existing ? null : "5K");
  const [distanceM, setDistanceM] = useState(existing?.race_distance_m || 5000);
  const [timeText, setTimeText] = useState(
    existing ? secToHMS(existing.race_time_seconds) : ""
  );
  const [raceLabel, setRaceLabel] = useState(existing?.race_label || "");

  const vdot = useMemo(() => {
    if (!existing) return null;
    return computeVdot(existing.race_distance_m, existing.race_time_seconds);
  }, [existing]);

  const paces = useMemo(() => trainingPaces(vdot), [vdot]);

  const startEdit = () => {
    if (existing) {
      setDistanceM(existing.race_distance_m);
      setTimeText(secToHMS(existing.race_time_seconds));
      setRaceLabel(existing.race_label || "");
      setPreset(null);
    } else {
      setDistanceM(5000);
      setTimeText("");
      setRaceLabel("");
      setPreset("5K");
    }
    setEditing(true);
  };

  const onPreset = (p) => {
    setPreset(p.label);
    if (p.meters) setDistanceM(p.meters);
  };

  const onSave = async () => {
    const seconds = parseMMSS(timeText);
    if (!seconds || !distanceM) {
      toast.error("Enter a valid distance and time (e.g. 27:00)");
      return;
    }
    setSaving(true);
    try {
      const updated = await updateCoachingAthlete(athlete.id, {
        pace_profile: {
          race_distance_m: Number(distanceM),
          race_time_seconds: seconds,
          race_label: raceLabel || null,
          updated_at: new Date().toISOString(),
        },
      });
      toast.success("Performance profile updated");
      onChange?.(updated);
      setEditing(false);
    } catch {
      toast.error("Couldn't save profile");
    } finally {
      setSaving(false);
    }
  };

  const onClear = async () => {
    if (!window.confirm("Remove the performance profile?")) return;
    setSaving(true);
    try {
      const updated = await updateCoachingAthlete(athlete.id, {
        pace_profile: null,
      });
      toast.success("Profile cleared");
      onChange?.(updated);
      setEditing(false);
    } catch {
      toast.error("Couldn't clear profile");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="border border-[var(--tir-border)] bg-white p-6"
      data-testid="pace-profile-card"
    >
      <div className="flex items-start justify-between mb-4 gap-4 flex-wrap">
        <div className="flex items-start gap-3">
          <Gauge size={18} className="text-[var(--tir-burgundy)] mt-1 shrink-0" />
          <div>
            <div className="font-serif-display text-xl text-[var(--tir-ink)]">
              Performance profile
            </div>
            <p className="text-sm text-[var(--tir-muted)] mt-0.5 max-w-xl">
              Drop in a recent race time to auto-compute VDOT, training paces, and race
              equivalents. The planner uses this to estimate workout duration from distance.
            </p>
          </div>
        </div>
        {!editing && (
          <button
            type="button"
            onClick={startEdit}
            data-testid="edit-pace-profile-btn"
            className="text-xs tracking-[0.22em] uppercase font-semibold text-[var(--tir-burgundy)] hover:text-[var(--tir-burgundy-dark)]"
          >
            {existing ? "Update" : "+ Add race time"}
          </button>
        )}
      </div>

      {editing ? (
        <div className="space-y-4">
          <div>
            <div className="text-[10px] tracking-[0.22em] uppercase text-[var(--tir-muted)] font-semibold mb-2">
              Recent race distance
            </div>
            <div className="flex flex-wrap gap-2">
              {RACE_PRESETS.map((p) => (
                <button
                  key={p.label}
                  type="button"
                  onClick={() => onPreset(p)}
                  className={`px-3 py-1.5 text-[11px] tracking-[0.2em] uppercase font-semibold border transition-colors ${
                    preset === p.label
                      ? "bg-[var(--tir-burgundy)] text-white border-[var(--tir-burgundy)]"
                      : "bg-white border-[var(--tir-border)] text-[var(--tir-ink)] hover:border-[var(--tir-burgundy)]"
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>
            {preset === "Custom" && (
              <div className="mt-3">
                <label className="block text-[9px] tracking-[0.22em] uppercase text-[var(--tir-muted)] font-semibold mb-1">
                  Custom distance (metres)
                </label>
                <input
                  type="number"
                  value={distanceM}
                  onChange={(e) => setDistanceM(parseFloat(e.target.value) || 0)}
                  className="w-full max-w-[200px] bg-white border border-[var(--tir-border)] focus:border-[var(--tir-burgundy)] py-1.5 px-2 text-sm focus:outline-none font-mono-tir"
                />
              </div>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-[9px] tracking-[0.22em] uppercase text-[var(--tir-muted)] font-semibold mb-1">
                Finish time (MM:SS or H:MM:SS)
              </label>
              <input
                value={timeText}
                onChange={(e) => setTimeText(e.target.value)}
                placeholder="27:00"
                data-testid="pace-profile-time-input"
                className="w-full bg-white border border-[var(--tir-border)] focus:border-[var(--tir-burgundy)] py-2 px-3 text-base focus:outline-none font-mono-tir"
              />
            </div>
            <div>
              <label className="block text-[9px] tracking-[0.22em] uppercase text-[var(--tir-muted)] font-semibold mb-1">
                Race label (optional)
              </label>
              <input
                value={raceLabel}
                onChange={(e) => setRaceLabel(e.target.value)}
                placeholder="Parkrun 5K — Apr 2026"
                className="w-full bg-white border border-[var(--tir-border)] focus:border-[var(--tir-burgundy)] py-2 px-3 text-base focus:outline-none"
              />
            </div>
          </div>
          <div className="flex items-center justify-between gap-3 pt-2">
            {existing ? (
              <button
                type="button"
                onClick={onClear}
                className="text-xs tracking-[0.22em] uppercase text-[var(--tir-muted)] hover:text-[var(--tir-burgundy)] font-semibold"
              >
                Remove profile
              </button>
            ) : (
              <span />
            )}
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setEditing(false)}
                className="text-xs tracking-[0.22em] uppercase text-[var(--tir-muted)] hover:text-[var(--tir-ink)] px-3 py-2"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={onSave}
                disabled={saving}
                data-testid="save-pace-profile-btn"
                className="inline-flex items-center gap-1.5 bg-[var(--tir-burgundy)] text-white px-4 py-2 text-xs tracking-[0.22em] uppercase font-semibold hover:bg-[var(--tir-burgundy-dark)] transition-colors disabled:opacity-50"
              >
                {saving ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      ) : !existing ? (
        <div className="text-sm italic text-[var(--tir-muted)] border-l-2 border-[var(--tir-border)] pl-4">
          No race time yet. Add a recent race to unlock pace auto-fill across the planner and week
          editor.
        </div>
      ) : (
        <div className="space-y-5">
          <div className="flex flex-wrap items-baseline gap-x-8 gap-y-3 border-b border-[var(--tir-border)] pb-4">
            <div>
              <div className="text-[10px] tracking-[0.22em] uppercase text-[var(--tir-muted)] font-semibold">
                Race
              </div>
              <div className="font-mono-tir text-lg text-[var(--tir-ink)] mt-0.5">
                {existing.race_label ||
                  `${formatDistanceLabel(existing.race_distance_m)} — ${secToHMS(
                    existing.race_time_seconds
                  )}`}
              </div>
              {existing.race_label && (
                <div className="font-mono-tir text-xs text-[var(--tir-muted)]">
                  {formatDistanceLabel(existing.race_distance_m)} ·{" "}
                  {secToHMS(existing.race_time_seconds)}
                </div>
              )}
            </div>
            <div>
              <div className="text-[10px] tracking-[0.22em] uppercase text-[var(--tir-muted)] font-semibold">
                VDOT
              </div>
              <div
                className="font-serif-display text-3xl text-[var(--tir-burgundy)] leading-none mt-0.5"
                data-testid="pace-profile-vdot"
              >
                {vdot ? vdot.toFixed(1) : "—"}
              </div>
            </div>
          </div>

          {paces && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <div className="text-[10px] tracking-[0.22em] uppercase text-[var(--tir-muted)] font-semibold mb-2">
                  Training paces (per km)
                </div>
                <ul className="divide-y divide-[var(--tir-border)] text-sm">
                  {[
                    ["easy", "Easy"],
                    ["marathon", "Marathon"],
                    ["threshold", "Threshold"],
                    ["interval", "Interval"],
                    ["repetition", "Repetition"],
                  ].map(([k, label]) => (
                    <li key={k} className="flex items-baseline justify-between py-1.5">
                      <span className="text-[var(--tir-ink)]">{label}</span>
                      <span
                        className="font-mono-tir text-[var(--tir-burgundy)]"
                        data-testid={`pace-row-${k}`}
                      >
                        {paceRangeLabel(paces[k])}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <div className="text-[10px] tracking-[0.22em] uppercase text-[var(--tir-muted)] font-semibold mb-2">
                  Race equivalents
                </div>
                <ul className="divide-y divide-[var(--tir-border)] text-sm">
                  {REFERENCE_RACES.map((r) => (
                    <li
                      key={r.key}
                      className="flex items-baseline justify-between py-1.5"
                    >
                      <span className="text-[var(--tir-ink)]">{r.label}</span>
                      <span
                        className="font-mono-tir text-[var(--tir-ink)]"
                        data-testid={`race-equiv-${r.key}`}
                      >
                        {secToHMS(predictRaceSeconds(vdot, r.meters))}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          <div className="text-[10px] italic text-[var(--tir-muted)]">
            Updated {existing.updated_at?.slice(0, 10) || ""} · Based on Jack Daniels' VDOT
            formula. Update the race time whenever the athlete runs a new PR.
          </div>
        </div>
      )}
    </div>
  );
}
